// AnnouncementsPage.jsx
// Full-feed announcements page with category tabs and composer (for admins).
import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { AuthContext } from "../../context/AuthContext";
import AnnouncementCard from "../../components/announcements/AnnouncementCard";
import AnnouncementComposer from "../../components/announcements/AnnouncementComposer";
import { Megaphone, Plus } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON     = "#00FF99";
const BLUE     = "#378ADD";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const CATS = [
  { value: "all",     label: "All" },
  { value: "general", label: "General" },
  { value: "policy",  label: "Policy" },
  { value: "event",   label: "Event" },
  { value: "urgent",  label: "Urgent" },
  { value: "kudos",   label: "Kudos" },
];

export default function AnnouncementsPage() {
  const { user } = useContext(AuthContext);
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat,     setCat]     = useState("all");
  const [showComposer, setShowComposer] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/announcements/list`, { headers: authHeaders() });
      const d   = res.ok ? await res.json() : [];
      setItems(Array.isArray(d) ? d : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("announcement_posted", h);
    return () => window.removeEventListener("announcement_posted", h);
  }, [load]);

  const filtered = useMemo(() => {
    if (cat === "all") return items;
    return items.filter((a) => (a.category || "general") === cat);
  }, [items, cat]);

  return (
    <MessengerLayout>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "rgba(0,255,153,0.1)", color: NEON,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: `0.5px solid ${NEON}33`,
          }}>
            <Megaphone size={16} />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Announcements</h1>
            <p style={{ fontSize: 11, color: "#666", margin: "2px 0 0 0" }}>
              {items.length} {items.length === 1 ? "post" : "posts"} · {filtered.length} shown
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowComposer((s) => !s)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "8px 14px", borderRadius: 8, fontSize: 12,
                background: "rgba(0,255,153,0.15)",
                border: `0.5px solid ${NEON}55`,
                color: NEON, cursor: "pointer",
              }}
            >
              <Plus size={13} /> {showComposer ? "Close" : "New post"}
            </button>
          )}
        </div>

        {/* Composer */}
        {showComposer && isAdmin && (
          <div style={{ marginBottom: 16 }}>
            <AnnouncementComposer
              endpoint="/api/admin/announcements"
              onPosted={() => { setShowComposer(false); load(); }}
            />
          </div>
        )}

        {/* Category tabs */}
        <div style={{
          display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16,
          borderBottom: "0.5px solid #1a1a1a", paddingBottom: 10,
        }}>
          {CATS.map((c) => {
            const active = c.value === cat;
            return (
              <button
                key={c.value}
                onClick={() => setCat(c.value)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 999,
                  fontSize: 11,
                  background: active ? `${BLUE}1a` : "transparent",
                  border: active ? `0.5px solid ${BLUE}77` : "0.5px solid #2a2a2a",
                  color: active ? BLUE : "#888",
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Feed */}
        {loading ? (
          <p style={{ color: "#555", fontSize: 12 }}>Loading…</p>
        ) : filtered.length === 0 ? (
          <div style={{
            padding: 30, textAlign: "center",
            border: "0.5px dashed #2a2a2a", borderRadius: 12,
          }}>
            <p style={{ color: "#666", fontSize: 13, margin: 0 }}>
              No announcements in this category yet.
            </p>
          </div>
        ) : (
          filtered.map((a) => (
            <AnnouncementCard
              key={a.id}
              announcement={a}
              onChanged={(updated) =>
                setItems((list) => list.map((x) => x.id === updated.id ? updated : x))
              }
              onDeleted={(id) =>
                setItems((list) => list.filter((x) => x.id !== id))
              }
            />
          ))
        )}
      </div>
    </MessengerLayout>
  );
}
